import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

function Insurances() {
  const [insurances, setInsurances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    insurance_type: 'life',
    duration_months: '12',
    description: ''
  });

  // Tarifs fixés par la banque - Marché le plus bas - 15%
  const insurancePricing = {
    life: { 
      premium: 42.49, 
      marketPrice: 49.99,
      coverage: 100000, 
      description: 'Protection financière complète pour votre famille' 
    },
    health: { 
      premium: 67.99, 
      marketPrice: 79.99,
      coverage: 50000, 
      description: 'Couverture médicale étendue avec hospitalisation' 
    },
    property: { 
      premium: 29.74, 
      marketPrice: 34.99,
      coverage: 200000, 
      description: 'Protection complète de votre habitation' 
    },
    auto: { 
      premium: 38.24, 
      marketPrice: 44.99,
      coverage: 20000, 
      description: 'Assurance tous risques pour votre véhicule' 
    },
    business: { 
      premium: 169.99, 
      marketPrice: 199.99,
      coverage: 500000, 
      description: 'Couverture professionnelle complète' 
    }
  };

  const token = localStorage.getItem('token');

  useEffect(() => {
    loadInsurances();
  }, []);

  const loadInsurances = async () => {
    try {
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const { data } = await axios.get(`${API_URL}/insurance`, config);
      setInsurances(data);
    } catch (err) {
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const pricing = insurancePricing[formData.insurance_type];
      
      await axios.post(`${API_URL}/insurance`, {
        ...formData,
        premium: pricing.premium,
        coverage: pricing.coverage,
        duration_months: parseInt(formData.duration_months),
        description: formData.description || pricing.description
      }, config);
      alert('Assurance souscrite avec succès !');
      setShowForm(false);
      setFormData({ insurance_type: 'life', duration_months: '12', description: '' });
      loadInsurances();
    } catch (err) {
      alert('Erreur: ' + (err.response?.data?.detail || err.message));
    }
  };

  if (loading) {
    return <div className="text-center py-20">Chargement...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold text-gray-900">Assurances</h1>
        <button
          onClick={() => setShowForm(!showForm)}
          className="px-6 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors"
        >
          {showForm ? 'Annuler' : 'Nouvelle assurance'}
        </button>
      </div>

      {/* Insurance Types */}
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-6 text-white">
          <div className="text-4xl mb-2">👤</div>
          <h3 className="font-bold text-xl mb-2">Assurance Vie</h3>
          <p className="text-sm text-blue-100">Protection financière complète pour votre famille</p>
        </div>
        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-6 text-white">
          <div className="text-4xl mb-2">🏥</div>
          <h3 className="font-bold text-xl mb-2">Assurance Santé</h3>
          <p className="text-sm text-green-100">Couverture médicale étendue avec hospitalisation</p>
        </div>
        <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-6 text-white">
          <div className="text-4xl mb-2">🏠</div>
          <h3 className="font-bold text-xl mb-2">Assurance Habitation</h3>
          <p className="text-sm text-purple-100">Protection complète de votre habitation</p>
        </div>
        <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl p-6 text-white">
          <div className="text-4xl mb-2">🚗</div>
          <h3 className="font-bold text-xl mb-2">Assurance Auto</h3>
          <p className="text-sm text-orange-100">Assurance tous risques pour votre véhicule</p>
        </div>
      </div>

      {/* Form */}
      {showForm && (
        <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Souscrire une assurance</h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Type d'assurance *
              </label>
              <select
                value={formData.insurance_type}
                onChange={(e) => setFormData({ ...formData, insurance_type: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="life">Assurance Vie</option>
                <option value="health">Assurance Santé</option>
                <option value="property">Assurance Habitation</option>
                <option value="auto">Assurance Automobile</option>
                <option value="business">Assurance Entreprise</option>
              </select>
            </div>

            {/* Description de l'assurance */}
            <div className="bg-blue-50 rounded-lg p-4 border-2 border-blue-200">
              <p className="text-sm font-semibold text-blue-900 mb-2">📋 Description</p>
              <p className="text-sm text-blue-700">
                {insurancePricing[formData.insurance_type].description}
              </p>
              <p className="text-xs text-blue-600 mt-3">
                Un conseiller vous contactera pour définir les conditions et le tarif adapté à votre situation.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Durée du contrat *
              </label>
              <select
                value={formData.duration_months}
                onChange={(e) => setFormData({ ...formData, duration_months: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="12">12 mois (1 an)</option>
                <option value="24">24 mois (2 ans)</option>
                <option value="36">36 mois (3 ans)</option>
                <option value="60">60 mois (5 ans)</option>
                <option value="120">120 mois (10 ans)</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Informations complémentaires (optionnel)
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                rows="3"
                placeholder="Précisions sur votre situation (adresse du bien, modèle de véhicule, etc.)"
              />
            </div>

            <button
              type="submit"
              className="w-full px-6 py-3 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 transition-colors"
            >
              Souscrire l'assurance
            </button>
          </form>
        </div>
      )}

      {/* Insurances List */}
      <div className="grid gap-6">
        {insurances.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm p-12 border border-gray-200 text-center">
            <p className="text-gray-500">Aucune assurance souscrite</p>
          </div>
        ) : (
          insurances.map((ins) => (
            <div
              key={ins.id}
              className="bg-white rounded-xl shadow-sm p-6 border border-gray-200"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <span className="text-3xl">
                    {ins.insurance_type === 'life' && '👤'}
                    {ins.insurance_type === 'health' && '🏥'}
                    {ins.insurance_type === 'property' && '🏠'}
                    {ins.insurance_type === 'auto' && '🚗'}
                    {ins.insurance_type === 'business' && '🏢'}
                  </span>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">
                      Assurance {ins.insurance_type === 'life' ? 'Vie' : 
                                ins.insurance_type === 'health' ? 'Santé' :
                                ins.insurance_type === 'property' ? 'Habitation' :
                                ins.insurance_type === 'auto' ? 'Automobile' : 'Entreprise'}
                    </h3>
                    <p className="text-sm text-gray-500">
                      <span className={`px-2 py-1 rounded-full text-xs ${
                        ins.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
                      }`}>
                        {ins.status}
                      </span>
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-sm text-gray-600">Couverture</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {ins.coverage?.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €
                  </p>
                </div>
              </div>

              <p className="text-gray-700 mb-4">{ins.description}</p>

              <div className="grid grid-cols-3 gap-4 pt-4 border-t border-gray-200">
                <div>
                  <p className="text-xs text-gray-600">Prime mensuelle</p>
                  <p className="font-semibold">{ins.premium?.toFixed(2)} €/mois</p>
                </div>
                <div>
                  <p className="text-xs text-gray-600">Date de début</p>
                  <p className="font-semibold">
                    {new Date(ins.start_date).toLocaleDateString('fr-FR')}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-600">Date de fin</p>
                  <p className="font-semibold">
                    {new Date(ins.end_date).toLocaleDateString('fr-FR')}
                  </p>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export default Insurances;
